<!--
  Warehouse Orders — TV display (wall-mounted, ~4m, no touch).
  Single column, large fonts, high contrast. No click handlers anywhere.

  Layout:
    1. Sticky header    : brand + live clock + daily/monthly progress bars
    2. KPI banner       : 4 counter cards (open / to-sign / prepared / value)
    3. In-progress      : list of openOrders
    4. Carriers pending : aggregate breakdown of `ordersToSign` by carrier
                          (v2.12.9 T2a+T2b — replaces the detailed
                          "Da firmare oggi" list, which was hidden
                          per PM request. Rationale: TV monitor should
                          show the carriers' pending load at-a-glance,
                          not the individual signature queue.)
    5. Closed today     : list of closedOrdersToday (only if any)

  v2.12.9 T2c: progressbar giornaliera/mensile e KPI "Preparati oggi"
  usano ora la semantica "concluso = preparato" (uscito da 'in
  lavorazione'), computata client-side dai getter effective*/prepared*.
  Vedi warehouse-orders.component.ts per il razionale completo.
-->

<div class="warehouse-display">

  <!-- ─────────────────────────────────────────────────────────────
       1. STICKY HEADER — identity + clock + objective progress.
       The header is the operators' glance-target while they walk
       past the screen, so the KPIs they care about most live here.
       ───────────────────────────────────────────────────────────── -->
  <header class="warehouse-header" role="banner" aria-label="Warehouse status">
    <div class="warehouse-header__brand">
      <div class="warehouse-header__logo">
        <img src="assets/icons/logo-diakonix-black.png" alt="logo" />
      </div>

      <div class="warehouse-header__content">
        <div class="warehouse-header__title">Diakonix</div>
        <div class="warehouse-header__subtitle">
          Magazzino · Lista Ordini
        </div>
      </div>
    </div>

    <!--<div class="warehouse-header__clock" aria-live="off">
      <div class="warehouse-header__time">{{ now | date: 'HH:mm:ss' }}</div>
      <div class="warehouse-header__date">{{ now | date: 'EEEE d MMMM y' }}</div>
    </div>-->

    <div class="warehouse-header__objectives">
      <!--
        Progressbar giornaliera + mensile (v2.12.9 T2c).
        Semantica: "concluso = preparato" — la % si incrementa appena
        l'ordine esce da "in lavorazione", non quando viene firmato dal
        corriere. Le formule sono client-side (getter effective*) per
        evitare dipendenza da endpoint BE che ancora conteggia sui
        chiusi. Target rimane BE-driven (progress.daily_total /
        monthly_total).
      -->

      <!-- Daily objective -->
      <div class="objective objective--daily">
        <div class="objective__label">
          Obiettivo giornaliero
          @if (isDailyExceeded) { <span class="objective__badge" aria-label="Superato">🎉</span> }
        </div>

        @if (loadingProgress && !progress) {
          <div class="objective__skeleton" aria-busy="true"></div>
        } @else if (progress) {
          <div class="objective__bar-wrap">
            <div
              class="objective__bar"
              [style.width.%]="effectiveDailyPercentage > 100 ? 100 : effectiveDailyPercentage"
              [class.objective__bar--full]="effectiveDailyPercentage >= 100 && !isDailyExceeded"
              [class.objective__bar--exceeded]="isDailyExceeded"
            ></div>
          </div>
          <div class="objective__values">
            <span class="objective__pct" [class.objective__pct--exceeded]="isDailyExceeded">{{ formatPercentage(effectiveDailyPercentage) }}%</span>
            <span class="objective__abs">
              <span class="objective__completed">{{ effectiveDailyProgress | currency : 'EUR' : 'symbol' : '1.2-2' }}</span> / {{ effectiveDailyTotal | currency : 'EUR' : 'symbol' : '1.2-2' }}
            </span>
          </div>
        } @else {
          <div class="objective__error">Dati non disponibili</div>
        }
      </div>

      <!-- Monthly objective -->
      <div class="objective objective--monthly">
        <div class="objective__label">
          Obiettivo mensile
          @if (isMonthlyExceeded) { <span class="objective__badge" aria-label="Superato">🏆</span> }
        </div>

        @if (loadingProgress && !progress) {
          <div class="objective__skeleton" aria-busy="true"></div>
        } @else if (progress) {
          <div class="objective__bar-wrap">
            <div
              class="objective__bar"
              [style.width.%]="effectiveMonthlyPercentage > 100 ? 100 : effectiveMonthlyPercentage"
              [class.objective__bar--full]="effectiveMonthlyPercentage >= 100 && !isMonthlyExceeded"
              [class.objective__bar--exceeded]="isMonthlyExceeded"
            ></div>
          </div>
          <div class="objective__values">
            <span class="objective__pct" [class.objective__pct--exceeded]="isMonthlyExceeded">{{ formatPercentage(effectiveMonthlyPercentage) }}%</span>
            <span class="objective__abs">
              <span class="objective__completed">{{ effectiveMonthlyProgress | currency : 'EUR' : 'symbol' : '1.2-2' }}</span> / {{ effectiveMonthlyTotal | currency : 'EUR' : 'symbol' : '1.2-2' }}
            </span>
          </div>
        } @else {
          <div class="objective__error">Dati non disponibili</div>
        }
      </div>
    </div>
  </header>

  <!-- ─────────────────────────────────────────────────────────────
       2. KPI BANNER — at-a-glance counters under the header.
       ───────────────────────────────────────────────────────────── -->
  <section class="kpi-banner" aria-label="Indicatori di giornata">
    <div class="kpi-card kpi-card--open">
      <div class="kpi-card__value">{{ openOrders.length }}</div>
      <div class="kpi-card__label">In lavorazione</div>
    </div>

    <div class="kpi-card kpi-card--sign">
      <div class="kpi-card__value">{{ ordersToSign.length }}</div>
      <div class="kpi-card__label">In attesa firma</div>
    </div>

    <!--
      Fix v2.13.1 (feedback PM 2026-07-09): dopo v2.12.9 T2c avevo
      rinominato queste 2 card in "Preparati oggi" / "Valore
      preparato oggi" con conteggio = to-sign preparati + chiusi.
      Il PM preferisce che le KPI qui riflettano gli ordini
      EFFETTIVAMENTE chiusi oggi (sync con la sezione "Chiusi
      oggi" sotto). La semantica "preparato" resta nella
      progressbar giornaliera in header — la motivazione visiva
      degli operatori vive lì, non nelle KPI count.
    -->
    <div class="kpi-card kpi-card--closed">
      <div class="kpi-card__value">{{ closedOrdersToday.length }}</div>
      <div class="kpi-card__label">Chiusi oggi</div>
    </div>

    <div class="kpi-card kpi-card--total">
      <div class="kpi-card__value">
        {{ totalNetClosedToday | currency: 'EUR':'symbol':'1.0-0' }}
      </div>
      <div class="kpi-card__label">Valore chiuso oggi</div>
    </div>
  </section>

  <!-- ─────────────────────────────────────────────────────────────
       3. IN-PROGRESS ORDERS — main list.
       ───────────────────────────────────────────────────────────── -->
  <section class="orders-section orders-section--in-progress" aria-label="Ordini in lavorazione">
    <div class="orders-section__title-row">
      <h2 class="orders-section__title">In lavorazione</h2>
      <span class="orders-section__count">{{ openOrders.length }}</span>
    </div>

    @if (openOrders.length === 0) {
      <div class="orders-empty">
        <div class="orders-empty__icon" aria-hidden="true">📭</div>
        <div class="orders-empty__msg">Nessun ordine in lavorazione.</div>
      </div>
    } @else {
      <div class="orders-table" role="list">
        <!-- Column header (only visible on the largest displays) -->
        <div class="orders-row orders-row--head" role="presentation">
          <div class="orders-row__num">#</div>
          <div class="orders-row__order">Ordine</div>
          <div class="orders-row__operator">Operatore</div>
          <div class="orders-row__progress">Avanzamento</div>
          <div class="orders-row__date">Data ordine</div>
        </div>

        @for (order of openOrders; track order.order_id; let i = $index) {
          @let pct =
            order.tab_order_status_cod === 100
              ? order.tab_order_status_previous_perc
              : order.tab_order_status_perc;
          <div
            class="orders-row"
            role="listitem"
            [class.orders-row--complete]="pct >= 100"
            [class.orders-row--high]="pct >= 80 && pct < 100"
            [class.orders-row--mid]="pct >= 30 && pct < 80"
            [class.orders-row--low]="pct < 30"
          >
            <div class="orders-row__num">
              <span class="num-bubble">{{ i + 1 }}</span>
            </div>
            <div class="orders-row__order">{{ order.or_order_number }}</div>
            <div class="orders-row__operator">
              {{ order.ac_first_name }} {{ order.ac_last_name }}
            </div>
            <div class="orders-row__progress">
              <div class="bar-track">
                <div class="bar-fill" [style.width.%]="pct"></div>
                <span class="bar-pct">{{ pct }}%</span>
              </div>
            </div>
            <div class="orders-row__date">
              {{ order.or_order_date | date: 'dd/MM/yyyy' }}
            </div>
          </div>
        }
      </div>
    }
  </section>

  <!-- ─────────────────────────────────────────────────────────────
       4. CARRIERS PENDING (v2.12.9 T2b).
       Sostituisce la lista dettagliata "Da firmare oggi" (T2a). Il PM
       ha chiesto di mostrare a colpo d'occhio quanti pacchi ha ogni
       corriere in attesa di ritiro, non l'elenco ordine per ordine.
       Sezione sempre visibile: se non c'è nulla in attesa mostra
       count 0 (layout stabile del TV monitor).
       ───────────────────────────────────────────────────────────── -->
  <section class="orders-section orders-section--carriers" aria-label="Corrieri in attesa di ritiro">
    <div class="orders-section__title-row">
      <h2 class="orders-section__title">🚚 Corrieri in attesa</h2>
      <span class="orders-section__count orders-section__count--warn">
        {{ ordersToSign.length }}
      </span>
    </div>

    @if (carrierBreakdown.length === 0) {
      <div class="orders-empty">
        <div class="orders-empty__icon" aria-hidden="true">✅</div>
        <div class="orders-empty__msg">Nessun ordine in attesa di corriere.</div>
      </div>
    } @else {
      <div class="carrier-grid" role="list">
        @for (carrier of carrierBreakdown; track carrier.name) {
          <!--
            Fix v2.13.1 (feedback PM 2026-07-09): rimosso il render del
            valore € da questa card. Il BE non popola or_net_value per
            gli ordini in "Da firmare" (endpoint getOrdersToSign) e
            l'workaround client-side (join+memoization) resta fragile
            in edge case. Il monitor magazzino serve principalmente
            a premiare gli OPERATORI (card verdi sotto) — i corrieri
            non hanno bisogno del € rank. Getter carrierBreakdown.value
            resta computato in TS per eventuale re-add futuro.
          -->
          <div class="carrier-card" role="listitem">
            <div class="carrier-card__name">{{ carrier.name }}</div>
            <div class="carrier-card__count">
              {{ carrier.count }}
              <span class="carrier-card__unit">
                {{ carrier.count === 1 ? 'ordine' : 'ordini' }}
              </span>
            </div>
          </div>
        }
      </div>
    }
  </section>

  <!-- ─────────────────────────────────────────────────────────────
       5. CLOSED TODAY (fix v2.13.1 · card operatori + medaglia).
       Sezione sempre visibile: se nulla chiuso oggi mostra empty
       state con emoji motivazionale. Card ordinate per valore €
       desc (chi ha fatturato di più oggi in cima), medaglia 🥇🥈🥉
       sulle prime 3 posizioni. Un ordine grosso vale più di 3
       piccoli — sort by value, non count.
       ───────────────────────────────────────────────────────────── -->
  <section class="orders-section orders-section--closed" aria-label="Ordini chiusi oggi per operatore">
    <div class="orders-section__title-row">
      <h2 class="orders-section__title">✅ Chiusi oggi</h2>
      <span class="orders-section__count orders-section__count--ok">
        {{ closedOrdersToday.length }}
      </span>
    </div>

    @if (operatorClosedBreakdown.length === 0) {
      <div class="orders-empty">
        <div class="orders-empty__icon" aria-hidden="true">💪</div>
        <div class="orders-empty__msg">Ancora nessun ordine chiuso oggi.</div>
      </div>
    } @else {
      <div class="operator-grid" role="list">
        @for (op of operatorClosedBreakdown; track op.name; let i = $index) {
          <div class="operator-card" role="listitem" [class.operator-card--top]="i < 3">
            @if (i === 0) { <span class="operator-card__medal" aria-label="Primo posto">🥇</span> }
            @else if (i === 1) { <span class="operator-card__medal" aria-label="Secondo posto">🥈</span> }
            @else if (i === 2) { <span class="operator-card__medal" aria-label="Terzo posto">🥉</span> }
            <div class="operator-card__name">{{ op.name }}</div>
            <div class="operator-card__count">
              {{ op.count }}
              <span class="operator-card__unit">
                {{ op.count === 1 ? 'ordine' : 'ordini' }}
              </span>
            </div>
            <div class="operator-card__value">
              {{ op.value | currency: 'EUR':'symbol':'1.0-0' }}
            </div>
          </div>
        }
      </div>
    }
  </section>

</div>
